import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "preact/hooks";

import { api } from "@/api/client";
import type { AiProvider, AiRouting, FetchGroup, FetchGroupProposal, SecretConfig, Settings } from "@/api/types";
import { ErrorState, LoadingState, SecretInput, useToast } from "@/components/ui";
import { readMinRelevanceScore, writeMinRelevanceScore } from "@/lib/uiPreferences";

const WEEKDAYS = [
  ["Mon", "周一"], ["Tue", "周二"], ["Wed", "周三"], ["Thu", "周四"],
  ["Fri", "周五"], ["Sat", "周六"], ["Sun", "周日"],
] as const;

type SettingsSection = "general" | "account" | "ai" | "zotero" | "fetch" | "groups";
const SETTINGS_SECTIONS: Array<[SettingsSection, string, string]> = [
  ["general", "通用", "◉"], ["account", "账号与同步", "♙"],
  ["ai", "AI 服务", "✦"], ["zotero", "Zotero 与 WebDAV", "Z"],
  ["fetch", "抓取与定时", "◷"], ["groups", "抓取组", "≡"],
];

function formatBytes(value: number): string {
  if (!value) return "0 B";
  const units = ["B", "KiB", "MiB", "GiB"];
  const index = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
  return `${(value / (1024 ** index)).toFixed(index ? 1 : 0)} ${units[index]}`;
}

function configured(secret: SecretConfig | undefined): boolean {
  return !secret?.clear_api_key && Boolean(secret?.api_key_configured || secret?.api_key);
}

function sanitizedSettings(settings: Settings): Settings {
  const chatProvider = settings.ai.routing?.chat || settings.ai.active;
  const sanitized: Settings = {
    ...settings,
    ai: {
      ...settings.ai,
      active: chatProvider,
      routing: {
        chat: chatProvider,
        tts: settings.ai.routing?.tts ?? settings.ai.active,
        asr: settings.ai.routing?.asr ?? settings.ai.active,
      },
      providers: Object.fromEntries(Object.entries(settings.ai.providers).map(([id, provider]) => [id, {
        ...provider,
        api_key: "",
        api_key_configured: configured(provider),
      }])),
    },
    zotero: {
      ...settings.zotero,
      web_api: {
        ...settings.zotero.web_api,
        api_key: "",
        api_key_configured: configured(settings.zotero.web_api),
      },
    },
    sync: {
      ...settings.sync,
      api_key: "",
      api_key_configured: configured(settings.sync),
    },
  };
  return Array.from(new Set(Object.values(sanitized.ai.routing)))
    .filter(Boolean)
    .reduce((current, id) => hydrateProvider(current, id), sanitized);
}

type AiCapability = keyof AiRouting;

function providerFromPreset(preset: AiProvider): AiProvider {
  return {
    base_url: preset.base_url ?? "",
    chat_model: preset.chat_model ?? "",
    tts_model: preset.tts_model ?? "",
    tts_voice: preset.tts_voice ?? "",
    asr_model: preset.asr_model ?? "",
    chat_protocol: preset.chat_protocol,
    tts_protocol: preset.tts_protocol,
    asr_protocol: preset.asr_protocol,
    audio_protocol: preset.audio_protocol,
    api_key: "",
    api_key_configured: false,
  };
}

function hydrateProvider(draft: Settings, id: string): Settings {
  if (!id || draft.ai.providers[id]) return draft;
  const preset = draft.ai_presets[id];
  if (!preset) return draft;
  return {
    ...draft,
    ai: {
      ...draft.ai,
      providers: { ...draft.ai.providers, [id]: providerFromPreset(preset) },
    },
  };
}

function routeCapability(draft: Settings, capability: AiCapability, id: string): Settings {
  const hydrated = hydrateProvider(draft, id);
  const routing = { ...hydrated.ai.routing, [capability]: id };
  return {
    ...hydrated,
    ai: { ...hydrated.ai, active: routing.chat, routing },
  };
}

function supportsCapability(
  id: string,
  provider: AiProvider | undefined,
  capability: AiCapability,
  preset?: AiProvider,
): boolean {
  if (id === "custom") return true;
  if (capability === "chat") return Boolean(provider?.chat_model || preset?.chat_model);
  if (capability === "tts") return Boolean(provider?.tts_model || preset?.tts_model);
  return Boolean(provider?.asr_model || preset?.asr_model);
}

function updateProvider(draft: Settings, id: string, patch: Partial<AiProvider>): Settings {
  return {
    ...draft,
    ai: {
      ...draft.ai,
      providers: {
        ...draft.ai.providers,
        [id]: { ...(draft.ai.providers[id] ?? {}), ...patch },
      },
    },
  };
}

function automaticFetchSignature(settings: Settings | undefined | null): string {
  const schedule = settings?.schedule;
  const groups = (settings?.fetch_groups ?? [])
    .filter((group) => !group.deleted && !group.deleted_at)
    .map((group, index) => [
      String(group.group_uuid ?? group.id ?? group.slug ?? `${group.name}-${index}`),
      Boolean(group.enabled),
    ] as const)
    .sort(([left], [right]) => left.localeCompare(right));
  return JSON.stringify({
    schedule: schedule ? {
      enabled: schedule.enabled,
      days: [...schedule.days].sort(),
      hour: schedule.hour,
      minute: schedule.minute,
    } : null,
    groups,
  });
}

export function SettingsView() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ["settings"],
    queryFn: api.settings,
    staleTime: 60_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
  const isDesktop = settingsQuery.data?.surface === "desktop";
  const isStandalone = settingsQuery.data?.surface === "standalone";
  const workspaceQuery = useQuery({
    queryKey: [
      "workspace-settings",
      settingsQuery.data?.sync.server ?? "",
      settingsQuery.data?.sync.username ?? "",
    ],
    queryFn: api.workspaceSettings,
    enabled: Boolean(isDesktop && settingsQuery.data?.cloud_connected),
    staleTime: 60_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
  const [draft, setDraft] = useState<Settings | null>(null);
  const [rulesText, setRulesText] = useState("[]");
  const [pairUsername, setPairUsername] = useState("");
  const [pairPassword, setPairPassword] = useState("");
  const [pairDevice, setPairDevice] = useState(() => `DailyResearch Mac · ${navigator.platform || "macOS"}`);
  const [pairing, setPairing] = useState(false);
  const [providerEditor, setProviderEditor] = useState("");
  const [minRelevanceScore, setMinRelevanceScore] = useState(readMinRelevanceScore);
  const audioCacheQuery = useQuery({ queryKey: ["audio-cache"], queryFn: api.audioCache, staleTime: 15_000 });
  const [section, setSection] = useState<SettingsSection>(() => {
    const saved = window.localStorage.getItem("dr_settings_section");
    return SETTINGS_SECTIONS.some(([id]) => id === saved) ? saved as SettingsSection : "general";
  });
  const [navCollapsed, setNavCollapsed] = useState(
    () => window.localStorage.getItem("dr_settings_nav_collapsed") === "1",
  );

  useEffect(() => {
    window.localStorage.setItem("dr_settings_section", section);
  }, [section]);
  useEffect(() => {
    window.localStorage.setItem("dr_settings_nav_collapsed", navCollapsed ? "1" : "0");
  }, [navCollapsed]);

  useEffect(() => {
    if (!settingsQuery.data) return;
    const usesCloudWorkspace = Boolean(isDesktop && settingsQuery.data.cloud_connected);
    if (usesCloudWorkspace && !workspaceQuery.data) return;
    const workspace = usesCloudWorkspace ? workspaceQuery.data! : settingsQuery.data;
    const safe = sanitizedSettings({
      ...workspace,
      sync: settingsQuery.data.sync,
      zotero: {
        ...workspace.zotero,
        url: settingsQuery.data.zotero.url,
      },
    });
    setDraft(safe);
    setPairUsername(safe.sync.username ?? "");
    setPairDevice(safe.sync.device_name || `DailyResearch Mac · ${navigator.platform || "macOS"}`);
    setRulesText(JSON.stringify(safe.zotero.collection_rules ?? [], null, 2));
  }, [settingsQuery.data, workspaceQuery.data, isDesktop]);

  const saveMutation = useMutation({
    mutationFn: async ({ target, payload }: { target: "local" | "workspace"; payload: Partial<Settings> }) => {
      if (target === "local" || isStandalone) return api.saveSettings(payload);
      return api.saveWorkspaceSettings(payload);
    },
    onSuccess: () => {
      writeMinRelevanceScore(minRelevanceScore);
      toast("设置已保存", "success");
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
      void queryClient.invalidateQueries({ queryKey: ["workspace-settings"] });
    },
    onError: (error) => toast(error instanceof Error ? error.message : "保存失败", "error"),
  });
  const clearAudioCacheMutation = useMutation({
    mutationFn: api.clearAudioCache,
    onSuccess: (result) => {
      toast(`已释放 ${formatBytes(result.evicted ? Math.max(0, (audioCacheQuery.data?.bytes ?? 0) - result.bytes) : 0)}`, "success");
      void queryClient.invalidateQueries({ queryKey: ["audio-cache"] });
    },
    onError: (error) => toast(error instanceof Error ? error.message : "清理失败", "error"),
  });

  const providerIds = useMemo(() => draft ? Array.from(new Set([
    ...Object.keys(draft.ai_presets), ...Object.keys(draft.ai.providers),
  ])) : [], [draft]);
  const editedProviderId = providerEditor || draft?.ai.routing.chat || "";
  const editedProvider = draft?.ai.providers[editedProviderId] ?? null;
  const usesCloudWorkspace = Boolean(isDesktop && settingsQuery.data?.cloud_connected);
  const settingsSaveTarget: "local" | "workspace" = isStandalone || (isDesktop && !settingsQuery.data?.cloud_connected)
    ? "local" : "workspace";
  const savedSettings = usesCloudWorkspace ? workspaceQuery.data : settingsQuery.data;
  const automaticFetchDirty = Boolean(
    draft && savedSettings
    && automaticFetchSignature(draft) !== automaticFetchSignature(savedSettings),
  );

  const selectProviderRoute = (capability: AiCapability, id: string): void => {
    setDraft(routeCapability(draft!, capability, id));
    // Keep the parameter form on the provider the user just selected. Without
    // this, a key typed after switching routes can silently update a previously
    // edited provider, leaving the newly selected route unusable.
    if (id) setProviderEditor(id);
  };

  if (settingsQuery.isPending) return <LoadingState label="正在读取设置…" />;
  if (settingsQuery.isError) return <ErrorState error={settingsQuery.error} retry={() => void settingsQuery.refetch()} />;
  if (usesCloudWorkspace && workspaceQuery.isPending) return <LoadingState label="正在读取云端工作区…" />;
  if (usesCloudWorkspace && workspaceQuery.isError) return <ErrorState error={workspaceQuery.error} retry={() => void workspaceQuery.refetch()} />;
  if (!draft) return <LoadingState label="正在准备设置表单…" />;

  const saveSection = (targetSection: SettingsSection): void => {
    if (targetSection === "general") {
      writeMinRelevanceScore(minRelevanceScore);
      saveMutation.mutate({ target: settingsSaveTarget, payload: {
        expected_revision: draft.revision, audio_cache_limit_mib: draft.audio_cache_limit_mib,
      } });
      return;
    }
    let rules: unknown[] = draft.zotero.collection_rules;
    if (targetSection === "zotero") {
      try {
        const parsed: unknown = JSON.parse(rulesText || "[]");
        if (!Array.isArray(parsed)) throw new Error("归档规则必须是数组");
        rules = parsed;
      } catch (error) {
        toast(error instanceof Error ? error.message : "归档规则不是有效 JSON", "error");
        return;
      }
    }
    const expected_revision = draft.revision;
    const payload: Partial<Settings> = targetSection === "ai" ? {
      expected_revision, ai: draft.ai,
    } : targetSection === "zotero" ? {
      expected_revision,
      zotero: {
        ...draft.zotero,
        collection_rules: rules,
      },
    } : {
      expected_revision, schedule: draft.schedule,
      relevance_threshold: draft.relevance_threshold,
      max_summaries_per_run: draft.max_summaries_per_run,
    };
    saveMutation.mutate({ target: isStandalone ? "local" : "workspace", payload });
  };

  const test = async (service: string): Promise<void> => {
    try {
      const result = !isStandalone && ["ai", "zotero"].includes(service)
        ? await api.testWorkspaceSetting(service)
        : await api.testSetting(service);
      toast(result.message ?? `${service} 连接成功`, "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : `${service} 连接失败`, "error");
    }
  };

  const pairSync = async (): Promise<void> => {
    if (!draft.sync.server.trim() || !pairUsername.trim() || !pairPassword || !pairDevice.trim()) {
      toast("安装配置缺少云端地址，或用户名/密码未填写", "error");
      return;
    }
    setPairing(true);
    try {
      const result = await api.pairSync({
        server: draft.sync.server.trim().replace(/\/+$/, ""),
        username: pairUsername.trim(),
        password: pairPassword,
        device_name: pairDevice.trim(),
      });
      setPairPassword("");
      void queryClient.invalidateQueries({ queryKey: ["sync"] });
      await settingsQuery.refetch({ throwOnError: true });
      await workspaceQuery.refetch({ throwOnError: true });
      const migration = result.migration;
      const migratedWorkspace = Boolean(
        migration?.default_replaced
        || migration?.groups_created?.length
        || migration?.groups_renamed?.length
        || migration?.settings_imported?.length,
      );
      toast(
        migratedWorkspace
          ? "配对成功，本地抓取配置已合并到云端"
          : "配对成功，设备令牌已写入系统 Keychain",
        "success",
      );
    } catch (error) {
      setPairPassword("");
      toast(error instanceof Error ? error.message : "配对失败", "error");
    } finally { setPairing(false); }
  };

  const cloudLocked = Boolean(isDesktop && !draft.cloud_connected);
  const saveButton = (target: SettingsSection) => (
    <button type="button" class="btn btn-primary" disabled={saveMutation.isPending || Boolean(isDesktop && workspaceQuery.isError)} onClick={() => saveSection(target)}>
      {saveMutation.isPending ? "保存中…" : "保存本节"}
    </button>
  );

  return (
    <main class="settings-page">
      <header class="page-heading settings-heading"><h1>设置</h1><p>{draft.workspace_mode === "cloud" ? "云端工作区已连接" : "本地工作区"} · 密钥只写入，从不回填。</p></header>
      <div class={`settings-shell${navCollapsed ? " nav-collapsed" : ""}`}>
        <aside class="settings-sidebar" aria-label="设置分类">
          <button type="button" class="settings-nav-collapse" aria-label={navCollapsed ? "展开设置分类" : "收起设置分类"} onClick={() => setNavCollapsed(!navCollapsed)}>{navCollapsed ? "›" : "‹ 收起"}</button>
          <nav>{SETTINGS_SECTIONS.map(([id, label, icon]) => <button key={id} type="button" class={section === id ? "active" : ""} aria-current={section === id ? "page" : undefined} title={label} onClick={() => setSection(id)}><span aria-hidden="true">{icon}</span><span class="settings-nav-label">{label}</span>{cloudLocked && ["ai", "zotero", "fetch"].includes(id) && <small aria-label="需要云端登录">锁</small>}</button>)}</nav>
        </aside>
        <select class="settings-mobile-select" aria-label="设置分类" value={section} onChange={(event) => setSection(event.currentTarget.value as SettingsSection)}>{SETTINGS_SECTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select>
        <div class="settings-content">
          {section === "general" && <section class="settings-card"><h2>通用</h2><label>论文列表最低相关性分数<input type="number" min="0" step="0.5" value={minRelevanceScore} onInput={(event) => setMinRelevanceScore(Math.max(0, Number(event.currentTarget.value) || 0))} /></label><p class="muted-text">只保存在当前浏览器或此 Mac，不影响抓取和 AI 总结。</p><hr /><h3>云端音频缓存</h3><p class="muted-text">音频可按需重新生成；正在播放的文件不会被删除。</p><div class="field-row"><label>容量上限（MiB）<input type="number" min="64" value={draft.audio_cache_limit_mib ?? 2048} onInput={(event) => setDraft({ ...draft, audio_cache_limit_mib: Math.max(64, Number(event.currentTarget.value) || 2048) })} /></label><p>{audioCacheQuery.data ? `${formatBytes(audioCacheQuery.data.bytes)} / ${formatBytes(audioCacheQuery.data.limit_bytes)}，可释放 ${formatBytes(audioCacheQuery.data.reclaimable_bytes)}；数据库备份 ${formatBytes(audioCacheQuery.data.backup_bytes)}` : "正在读取缓存用量…"}</p></div><div class="actions"><button type="button" class="btn btn-ghost" disabled={clearAudioCacheMutation.isPending} onClick={() => { if (window.confirm("释放全部可再生成的音频缓存？后续播放时会自动重新生成。")) clearAudioCacheMutation.mutate(); }}>{clearAudioCacheMutation.isPending ? "清理中…" : "释放音频缓存"}</button>{saveButton("general")}</div></section>}

          {section === "account" && <section class="settings-card account-sync-card"><h2>账号与同步</h2>{isDesktop ? <>{draft.cloud_connected ? <div class="pair-status connected" role="status"><strong>已连接阿里云</strong><span>{draft.sync.username || "已配对账号"}</span></div> : <><div class="pair-status" role="status">离线使用，云端 AI、定时抓取和多设备同步暂不可用。</div><p class="muted-text">云端地址由 App 安装配置提供；设备名将自动登记为“{pairDevice}”。</p><label>云端用户名<input autoComplete="username" value={pairUsername} onInput={(event) => setPairUsername(event.currentTarget.value)} /></label><label>云端密码<input type="password" autoComplete="current-password" value={pairPassword} onInput={(event) => setPairPassword(event.currentTarget.value)} /></label><button type="button" class="btn btn-primary" disabled={pairing} onClick={() => void pairSync()}>{pairing ? "登录中…" : "登录阿里云"}</button></>}<button type="button" class="btn btn-ghost" disabled={!draft.cloud_connected} onClick={() => void test("sync")}>测试同步</button></> : <><p>当前云端账号的密码和设备在右上角账号菜单中管理。</p><p class="muted-text">账号配置与设备令牌不会因设置页升级而重置。</p></>}</section>}

          {section === "ai" && (cloudLocked ? <LockedCard title="AI 服务需要云端账号" onLogin={() => setSection("account")} /> : <section class="settings-card"><h2>{isStandalone ? "独立工作区" : "云端工作区"} · AI 服务</h2><p class="muted-text">不同能力可使用不同 Provider；语音能力也可以单独禁用。</p><div class="field-row"><ProviderRouteSelect label="对话 / 总结 Provider" capability="chat" value={draft.ai.routing.chat} providerIds={providerIds} settings={draft} onChange={(id) => selectProviderRoute("chat", id)} /><ProviderRouteSelect label="语音合成（TTS）Provider" capability="tts" value={draft.ai.routing.tts} providerIds={providerIds} settings={draft} allowDisabled onChange={(id) => selectProviderRoute("tts", id)} /><ProviderRouteSelect label="语音识别（ASR）Provider" capability="asr" value={draft.ai.routing.asr} providerIds={providerIds} settings={draft} allowDisabled onChange={(id) => selectProviderRoute("asr", id)} /></div><label>编辑 Provider 参数<select value={editedProviderId} onChange={(event) => { const id = event.currentTarget.value; setProviderEditor(id); setDraft(hydrateProvider(draft, id)); }}>{providerIds.map((id) => <option key={id} value={id}>{draft.ai_presets[id]?.label ?? id}</option>)}</select></label>{editedProvider && <ProviderEditor key={editedProviderId} providerId={editedProviderId} provider={editedProvider} preset={draft.ai_presets[editedProviderId]} onChange={(patch) => setDraft(updateProvider(draft, editedProviderId, patch))} />}<div class="actions"><button type="button" class="btn btn-ghost" onClick={() => void test("ai")}>测试已保存的对话配置</button>{saveButton("ai")}</div></section>)}

          {section === "zotero" && (cloudLocked ? <section class="settings-card"><h2>Zotero 本地 Connector</h2><p>未登录云端时，DailyResearch 会在 Zotero 打开后补发条目、PDF，并使用已经存在的本地 collection。</p><p class="muted-text">新建 collection、PDF 批注和精读 Note 需要配置 Zotero Web API。</p><button type="button" class="btn btn-primary" onClick={() => setSection("account")}>登录阿里云</button></section> : <section class="settings-card"><h2>Zotero 与 WebDAV</h2><div class="delivery-status"><strong>当前通道</strong><span>{deliveryLabel(draft.zotero.delivery)}</span>{draft.zotero.last_test && <small>最近测试：{draft.zotero.last_test.ok ? "正常" : "异常"} · {draft.zotero.last_test.message}</small>}</div><label>默认 collection<input value={draft.zotero.collection} onInput={(event) => setDraft({ ...draft, zotero: { ...draft.zotero, collection: event.currentTarget.value } })} /></label><label>Web API userID<input value={draft.zotero.web_api.user_id ?? ""} onInput={(event) => setDraft({ ...draft, zotero: { ...draft.zotero, web_api: { ...draft.zotero.web_api, user_id: event.currentTarget.value } } })} /></label><SecretInput label="Zotero Web API Key" configured={configured(draft.zotero.web_api)} value={draft.zotero.web_api.api_key ?? ""} onInput={(value) => setDraft({ ...draft, zotero: { ...draft.zotero, web_api: { ...draft.zotero.web_api, api_key: value, clear_api_key: false } } })} onClear={() => setDraft({ ...draft, zotero: { ...draft.zotero, web_api: { ...draft.zotero.web_api, api_key: "", api_key_configured: false, clear_api_key: true } } })} clearConfirmation="清除 Zotero Web API 配置？系统将切换到本地 Connector；新 collection、PDF 批注和精读 Note 会暂停，直到重新配置 Web API。" /><label class="check-label"><input type="checkbox" checked={draft.zotero.annotations_enabled} onChange={(event) => setDraft({ ...draft, zotero: { ...draft.zotero, annotations_enabled: event.currentTarget.checked } })} />启用 PDF 五色批注</label><CollectionRulesEditor value={rulesText} onChange={setRulesText} /><div class="actions"><button type="button" class="btn btn-ghost" onClick={() => void test("zotero")}>测试 Zotero</button>{saveButton("zotero")}</div><div class="webdav-summary"><h3>WebDAV 文件服务</h3><p>{draft.delivery?.webdav_enabled ? "由阿里云提供，凭据请在云端账号页管理。" : "服务器尚未启用 WebDAV。"}</p>{draft.delivery?.webdav_public_url && <code>{draft.delivery.webdav_public_url}</code>}</div></section>)}

          {section === "fetch" && (cloudLocked ? <LockedCard title="定时抓取需要云端账号" onLogin={() => setSection("account")}><p>离线工作区仍可从顶部“抓取”按钮手动执行。</p></LockedCard> : <section class="settings-card"><h2>抓取与定时</h2><p class="muted-text">自动执行 = 开启云端定时抓取 + 至少一个已启用抓取组 + 至少一个重复日期。立即抓取不受定时开关影响。</p>{savedSettings && <AutomaticFetchStatus settings={savedSettings} dirty={automaticFetchDirty} />}<p class="muted-text">北京时间执行；论文日期按 arXiv UTC submittedDate 记录。只有严格高于准入阈值的论文会入库，并立即生成总结和音频。</p><div class="field-row"><label>初筛准入阈值<input type="number" step="0.5" value={draft.relevance_threshold} onInput={(event) => setDraft({ ...draft, relevance_threshold: Number(event.currentTarget.value) || 0 })} /></label></div>{draft.schedule && <><label class="check-label"><input type="checkbox" checked={draft.schedule.enabled} onChange={(event) => setDraft({ ...draft, schedule: { ...draft.schedule!, enabled: event.currentTarget.checked } })} />启用云端定时抓取</label><div class="field-row"><label>小时<input type="number" min="0" max="23" value={draft.schedule.hour} onInput={(event) => setDraft({ ...draft, schedule: { ...draft.schedule!, hour: Number(event.currentTarget.value) } })} /></label><label>分钟<input type="number" min="0" max="59" value={draft.schedule.minute} onInput={(event) => setDraft({ ...draft, schedule: { ...draft.schedule!, minute: Number(event.currentTarget.value) } })} /></label></div><fieldset class="weekday-fieldset"><legend>重复日期</legend>{WEEKDAYS.map(([value, label]) => <label class="check-label" key={value}><input type="checkbox" checked={draft.schedule!.days.includes(value)} onChange={(event) => setDraft({ ...draft, schedule: { ...draft.schedule!, days: event.currentTarget.checked ? [...draft.schedule!.days, value] : draft.schedule!.days.filter((day) => day !== value) } })} />{label}</label>)}</fieldset></>}{saveButton("fetch")}</section>)}

          {section === "groups" && <FetchGroups groups={draft.fetch_groups ?? []} savedGroups={savedSettings?.fetch_groups ?? []} workspace={!isStandalone && Boolean(draft.cloud_connected)} agentEnabled={Boolean(draft.capabilities?.cloud_ai)} onChange={(groups) => setDraft({ ...draft, fetch_groups: groups })} />}
        </div>
      </div>
    </main>
  );
}

function ProviderRouteSelect({
  label, capability, value, providerIds, settings, allowDisabled = false, onChange,
}: {
  label: string;
  capability: AiCapability;
  value: string;
  providerIds: string[];
  settings: Settings;
  allowDisabled?: boolean;
  onChange: (id: string) => void;
}) {
  const available = providerIds.filter((id) => id === value || supportsCapability(
    id, settings.ai.providers[id], capability, settings.ai_presets[id],
  ));
  return <label>{label}<select value={value} onChange={(event) => onChange(event.currentTarget.value)}>{allowDisabled && <option value="">禁用</option>}{available.map((id) => <option key={id} value={id}>{settings.ai_presets[id]?.label ?? id}</option>)}</select></label>;
}

function ProviderEditor({
  providerId, provider, preset, onChange,
}: {
  providerId: string;
  provider: AiProvider;
  preset?: AiProvider;
  onChange: (patch: Partial<AiProvider>) => void;
}) {
  const custom = providerId === "custom";
  const chat = custom || supportsCapability(providerId, provider, "chat", preset);
  const tts = custom || supportsCapability(providerId, provider, "tts", preset);
  const asr = custom || supportsCapability(providerId, provider, "asr", preset);
  const capabilities = [chat && "对话 / 总结", tts && "语音合成", asr && "语音识别"].filter(Boolean).join("、") || "无";
  return <section class="provider-editor"><p class="muted-text">支持能力：{capabilities}</p><label>Base URL<input value={provider.base_url ?? ""} onInput={(event) => onChange({ base_url: event.currentTarget.value })} /></label>{chat && <label>对话 / 总结模型<input value={provider.chat_model ?? ""} onInput={(event) => onChange({ chat_model: event.currentTarget.value })} /></label>}{tts && <div class="field-row"><label>语音合成模型<input value={provider.tts_model ?? ""} onInput={(event) => onChange({ tts_model: event.currentTarget.value })} /></label><label>音色<input value={provider.tts_voice ?? ""} onInput={(event) => onChange({ tts_voice: event.currentTarget.value })} /></label></div>}{asr && <label>语音识别模型<input value={provider.asr_model ?? ""} onInput={(event) => onChange({ asr_model: event.currentTarget.value })} /></label>}<SecretInput label="API Key" configured={configured(provider)} value={provider.api_key ?? ""} onInput={(value) => onChange({ api_key: value, clear_api_key: false })} onClear={() => onChange({ api_key: "", api_key_configured: false, clear_api_key: true })} /><details><summary>高级协议设置</summary>{chat && <label>对话协议<input value={provider.chat_protocol ?? "openai"} onInput={(event) => onChange({ chat_protocol: event.currentTarget.value })} /></label>}{tts && <label>语音合成协议<input value={provider.tts_protocol ?? provider.audio_protocol ?? "openai"} onInput={(event) => onChange({ tts_protocol: event.currentTarget.value })} /></label>}{asr && <label>语音识别协议<input value={provider.asr_protocol ?? provider.audio_protocol ?? "openai"} onInput={(event) => onChange({ asr_protocol: event.currentTarget.value })} /></label>}</details></section>;
}

function AutomaticFetchStatus({ settings, dirty }: { settings: Settings; dirty: boolean }) {
  const schedule = settings.schedule;
  const activeGroups = (settings.fetch_groups ?? []).filter(
    (group) => group.enabled && !group.deleted && !group.deleted_at,
  );
  const cloud = settings.workspace_mode === "cloud" || settings.surface === "cloud";
  const scope = cloud ? "云端" : "";
  const hasDays = Boolean(schedule?.days.length);
  const effective = Boolean(schedule?.enabled && hasDays && activeGroups.length);
  let title = `${scope}自动抓取未开启`;
  let detail = activeGroups.length
    ? `已有 ${activeGroups.length} 个已启用抓取组；它们仍可手动抓取。要自动执行，还需开启${scope}定时。`
    : "请先开启定时、选择重复日期，并至少启用一个抓取组。";
  if (schedule?.enabled && !hasDays) {
    title = `${scope}定时已开启，但不会自动抓取`;
    detail = "尚未选择任何重复日期。";
  } else if (schedule?.enabled && activeGroups.length === 0) {
    title = `${scope}定时已开启，但不会自动抓取`;
    detail = "目前没有已启用的抓取组。";
  } else if (effective && schedule) {
    const dayLabels = schedule.days.map(
      (day) => WEEKDAYS.find(([value]) => value === day)?.[1] ?? day,
    ).join("、");
    const time = `${String(schedule.hour).padStart(2, "0")}:${String(schedule.minute).padStart(2, "0")}`;
    title = `${scope}自动抓取配置已开启`;
    detail = `已保存：${dayLabels} ${time}（北京时间）执行 ${activeGroups.length} 个已启用抓取组${cloud ? "；Mac 关闭不影响云端执行" : ""}。`;
  }
  return <div class={`pair-status${effective ? " connected" : ""}`} role="status" aria-label="自动抓取状态"><strong>{title}</strong><span>{detail}</span>{dirty && <small>表单有未保存更改；以上显示的是当前已保存的{scope || "本地"}配置。</small>}</div>;
}

function deliveryLabel(value: Settings["zotero"]["delivery"]): string {
  if (value === "cloud_webdav") return "阿里云 Web API + WebDAV";
  if (value === "cloud_web_api") return "阿里云 Zotero Web API";
  if (value === "waiting_zotero") return "等待本地 Zotero";
  if (value === "api_error") return "Zotero API 配置异常";
  return "本地 Zotero Connector";
}

function LockedCard({ title, onLogin, children }: { title: string; onLogin: () => void; children?: preact.ComponentChildren }) {
  return <section class="settings-card locked-card"><h2>{title}</h2>{children}<p class="muted-text">登录后由阿里云 Worker 安全执行，Mac 关闭也不影响任务。</p><button type="button" class="btn btn-primary" onClick={onLogin}>登录阿里云</button></section>;
}

function CollectionRulesEditor({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  let rules: Array<Record<string, unknown>> = [];
  try { const parsed: unknown = JSON.parse(value || "[]"); if (Array.isArray(parsed)) rules = parsed as Array<Record<string, unknown>>; } catch { /* validation is shown on save */ }
  const update = (index: number, patch: Record<string, unknown>): void => onChange(JSON.stringify(rules.map((rule, current) => current === index ? { ...rule, ...patch } : rule), null, 2));
  return <section class="collection-rules"><div class="section-title-row"><div><h3>Collection 分类规则</h3><p>AI 建议必须确认后才生效；旧规则保持原顺序。</p></div><button type="button" class="btn btn-ghost" onClick={() => onChange(JSON.stringify([...rules, { collection: "", keywords: [], confirmed: true, priority: 0 }], null, 2))}>新增规则</button></div>{rules.map((rule, index) => <article class="collection-rule" key={index}><label>Collection 路径<input value={String(rule.collection ?? "")} onInput={(event) => update(index, { collection: event.currentTarget.value })} /></label><label>关键词（逗号分隔）<input value={Array.isArray(rule.keywords) ? rule.keywords.join(", ") : ""} onInput={(event) => update(index, { keywords: event.currentTarget.value.split(",").map((item) => item.trim()).filter(Boolean) })} /></label><label class="check-label"><input type="checkbox" checked={rule.confirmed !== false} onChange={(event) => update(index, { confirmed: event.currentTarget.checked })} />已确认并启用</label><button type="button" class="btn btn-ghost" onClick={() => onChange(JSON.stringify(rules.filter((_, current) => current !== index), null, 2))}>删除</button></article>)}<details><summary>高级 JSON 导入/导出</summary><label>规则 JSON<textarea rows={8} value={value} onInput={(event) => onChange(event.currentTarget.value)} /></label></details></section>;
}

function FetchGroups({ groups, savedGroups, workspace, agentEnabled, onChange }: { groups: FetchGroup[]; savedGroups: FetchGroup[]; workspace: boolean; agentEnabled: boolean; onChange: (groups: FetchGroup[]) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [history, setHistory] = useState<Array<Record<string, unknown>> | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [feedback, setFeedback] = useState("");
  const [proposal, setProposal] = useState<FetchGroupProposal | null>(null);
  const [agentJobId, setAgentJobId] = useState<number | null>(null);
  const [savedSlugs, setSavedSlugs] = useState<Record<string, string>>({});
  const agentJob = useQuery({
    queryKey: ["fetch-group-suggestion", agentJobId],
    queryFn: () => api.job(agentJobId!, workspace),
    enabled: agentJobId !== null,
    refetchInterval: (query) => query.state.data?.running ? 1_000 : false,
  });

  useEffect(() => {
    setSavedSlugs((current) => {
      const next = { ...current };
      groups.forEach((group) => { if (group.group_uuid && group.slug && !next[group.group_uuid]) next[group.group_uuid] = group.slug; });
      return next;
    });
  }, [groups]);
  useEffect(() => {
    if (!agentJobId || !agentJob.data || agentJob.data.running) return;
    if (agentJob.data?.state === "succeeded" && agentJob.data.result) {
      setProposal(agentJob.data.result as unknown as FetchGroupProposal);
      setFeedback("");
      toast("Agent 草案已生成，请检查并人工确认", "success");
    } else if (agentJob.data?.state === "failed") {
      toast(agentJob.data.error || "Agent 草案生成失败", "error");
    }
    setAgentJobId(null);
  }, [agentJobId, agentJob.data, toast]);

  const visibleGroups = groups.filter((group) => showDeleted ? Boolean(group.deleted_at || group.deleted) : !group.deleted_at && !group.deleted);
  const selected = visibleGroups[selectedIndex] ?? visibleGroups[0];
  const selectedSourceIndex = selected ? groups.indexOf(selected) : -1;
  const identifier = (group: FetchGroup): string | number | undefined => group.group_uuid || group.id;
  const selectedIdentifier = selected ? identifier(selected) : undefined;
  const savedSelected = selectedIdentifier === undefined ? undefined : savedGroups.find(
    (group) => identifier(group) === selectedIdentifier,
  );
  const canRun = Boolean(selected?.enabled && savedSelected?.enabled);
  const invalidateSettingsCache = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["settings"] });
    if (workspace) void queryClient.invalidateQueries({ queryKey: ["workspace-settings"] });
  };
  const refresh = async (includeDeleted = showDeleted): Promise<void> => {
    const fresh = await api.fetchGroups(includeDeleted, workspace);
    onChange(fresh);
    setSelectedIndex(0);
    invalidateSettingsCache();
  };
  const toggleDeleted = async (): Promise<void> => {
    const next = !showDeleted;
    try {
      const fresh = await api.fetchGroups(next, workspace);
      onChange(fresh);
      setShowDeleted(next);
      setSelectedIndex(0);
      setHistory(null);
    } catch (error) { toast(error instanceof Error ? error.message : "抓取组列表读取失败", "error"); }
  };
  const update = (patch: Partial<FetchGroup>): void => {
    if (selectedSourceIndex >= 0) onChange(groups.map((group, current) => current === selectedSourceIndex ? { ...group, ...patch } : group));
  };
  const save = async (): Promise<void> => {
    if (!selected || selectedSourceIndex < 0) return;
    try {
      let preserve_old_slug = true;
      const oldSlug = selected.group_uuid ? savedSlugs[selected.group_uuid] : undefined;
      if (oldSlug && oldSlug !== selected.slug) {
        preserve_old_slug = window.confirm(`是否保留旧 slug“${oldSlug}”作为别名？\n\n保留后其他抓取组不能再使用它；取消则立即释放。`);
      }
      const payload = { ...selected, preserve_old_slug };
      const saved = workspace ? await api.saveWorkspaceFetchGroup(payload) : await api.saveFetchGroup(payload);
      const updated = { ...selected, id: saved.id ?? selected.id, group_uuid: saved.group_uuid ?? selected.group_uuid, slug: saved.slug ?? selected.slug, version: saved.version ?? selected.version };
      onChange(groups.map((current, index) => index === selectedSourceIndex ? updated : current));
      if (updated.group_uuid) setSavedSlugs((current) => ({ ...current, [updated.group_uuid!]: updated.slug }));
      invalidateSettingsCache();
      toast(selected.group_uuid || selected.id ? "抓取组已保存" : "草案已确认并创建", "success");
    } catch (error) { toast(error instanceof Error ? error.message : "保存失败", "error"); }
  };
  const remove = async (): Promise<void> => {
    if (!selected || selectedSourceIndex < 0) return;
    if (!window.confirm(`删除“${selected.name}”？它会停止抓取，但日期账本和论文关联仍可恢复。`)) return;
    try {
      const target = identifier(selected);
      if (target !== undefined) {
        if (workspace) await api.deleteWorkspaceFetchGroup(target, selected.version);
        else await api.deleteFetchGroup(target, selected.version);
        await refresh(false);
      } else {
        onChange(groups.filter((_, index) => index !== selectedSourceIndex));
      }
      toast("抓取组已移入已删除列表", "success");
    } catch (error) { toast(error instanceof Error ? error.message : "删除失败", "error"); }
  };
  const restore = async (): Promise<void> => {
    if (!selected?.group_uuid) return;
    try { await api.restoreFetchGroup(selected.group_uuid, workspace); await refresh(true); toast("抓取组已恢复", "success"); }
    catch (error) { toast(error instanceof Error ? error.message : "恢复失败", "error"); }
  };
  const purge = async (): Promise<void> => {
    if (!selected?.group_uuid || !window.confirm(`永久清除“${selected.name}”？\n\n组、日期账本和论文关联将不可恢复，但论文不会被删除。`)) return;
    try { await api.purgeFetchGroup(selected.group_uuid, workspace); await refresh(true); toast("抓取组已永久清除；论文保留", "success"); }
    catch (error) { toast(error instanceof Error ? error.message : "永久清除失败", "error"); }
  };
  const run = async (): Promise<void> => {
    if (!canRun) { toast("请先启用并保存这个抓取组", "error"); return; }
    const target = selectedIdentifier;
    if (target === undefined) { toast("请先保存这个抓取组", "error"); return; }
    try { const result = await api.fetchGroupNow(target, workspace); toast(result.message ?? "抓取任务已排队", "success"); }
    catch (error) { toast(error instanceof Error ? error.message : "抓取失败", "error"); }
  };
  const loadHistory = async (): Promise<void> => {
    const target = selected && identifier(selected);
    if (target === undefined) { toast("请先保存这个抓取组", "error"); return; }
    try { const result = await api.fetchGroupHistory(target, workspace); setHistory(result.items); }
    catch (error) { toast(error instanceof Error ? error.message : "日期记录读取失败", "error"); }
  };
  const create = (seed?: FetchGroupProposal): void => {
    const nextGroup: FetchGroup = seed ? { ...seed, group_uuid: undefined, id: undefined, version: undefined, deleted_at: "", deleted: false, enabled: true } : {
      name: "新抓取组", slug: "new-research", enabled: true, categories: [], keywords: {}, exclude_keywords: [], research_profile: "", backfill_enabled: true, backfill_initial_days: 30, backfill_step_days: 1,
    };
    const next = [...groups, nextGroup];
    onChange(next);
    setShowDeleted(false);
    setSelectedIndex(next.filter((group) => !group.deleted_at && !group.deleted).length - 1);
    setProposal(null);
    setAgentOpen(false);
  };
  const copy = (): void => {
    if (!selected) return;
    create({ ...selected, name: `${selected.name} 副本`, slug: `${selected.slug || "research-group"}-copy`, reasoning: "复制自现有抓取组" });
  };
  const askAgent = async (): Promise<void> => {
    if (!agentEnabled) { toast("Mac 离线时无法使用 Agent，请先登录阿里云", "error"); return; }
    try {
      const queued = await api.suggestFetchGroup({ description: description.trim(), feedback: feedback.trim(), previous_proposal: proposal ?? undefined }, workspace);
      setAgentJobId(queued.job_id);
    } catch (error) { toast(error instanceof Error ? error.message : "Agent 任务创建失败", "error"); }
  };

  return <section class="settings-card fetch-groups-panel">
    <div class="section-title-row"><div><h2>抓取组</h2><p>每组独立推进昨天线与历史线；自动执行还需在“抓取与定时”开启云端定时，立即抓取不受定时开关影响。</p></div><div class="actions"><button type="button" class="btn btn-primary" disabled={!agentEnabled} title={!agentEnabled ? "登录阿里云后可用" : undefined} onClick={() => setAgentOpen(!agentOpen)}>Agent 创建</button><button type="button" class="btn btn-ghost" onClick={() => create()}>手动新建</button><button type="button" class="btn btn-ghost" onClick={() => void toggleDeleted()}>{showDeleted ? "查看活动组" : "查看已删除"}</button></div></div>
    {agentOpen && <section class="fetch-group-agent" aria-label="Agent 创建抓取组"><label>你希望长期关注什么？<textarea rows={5} minLength={20} maxLength={5000} placeholder="尽可能自然地描述研究主题、关注的方法或数据集，以及不想看到的内容。" value={description} onInput={(event) => setDescription(event.currentTarget.value)} /></label><small>{description.length}/5000（至少 20 字）</small>{proposal && <><div class="agent-proposal"><strong>{proposal.name}</strong><code>{proposal.slug}</code><p>{proposal.research_profile}</p><small>分类 {proposal.categories.join(", ")} · 置信度 {Math.round((proposal.confidence ?? 0) * 100)}%</small>{proposal.reasoning && <small>配置理由：{proposal.reasoning}</small>}{proposal.scope_risk && <small>范围风险：{proposal.scope_risk}</small>}</div><label>追加意见<textarea rows={3} maxLength={2000} value={feedback} onInput={(event) => setFeedback(event.currentTarget.value)} placeholder="例如：减少纯视觉内容，更关注恶劣天气数据集" /></label></>}<div class="actions"><button type="button" class="btn btn-primary" disabled={description.trim().length < 20 || agentJobId !== null} onClick={() => void askAgent()}>{agentJobId ? "Agent 正在整理…" : proposal ? "让 Agent 再优化" : "生成草案"}</button>{proposal && <button type="button" class="btn btn-ghost" onClick={() => create(proposal)}>采用草案并编辑</button>}</div><p class="muted-text">Agent 只生成草案，不会直接写入数据库；采用后仍需在右侧确认创建。</p></section>}
    <div class="fetch-groups-layout">
      <nav class="fetch-group-list" aria-label={showDeleted ? "已删除抓取组" : "抓取组列表"}>{visibleGroups.length === 0 && <p class="muted-text">{showDeleted ? "没有已删除抓取组" : "目前没有活动抓取组，定时抓取会自动停止。"}</p>}{visibleGroups.map((group, index) => <button type="button" key={group.group_uuid ?? group.id ?? `new-${index}`} class={index === selectedIndex ? "active" : ""} onClick={() => { setSelectedIndex(index); setHistory(null); }}><span><strong>{group.name}</strong><code>{group.slug || "旧版抓取组"}</code><small>{group.deleted_at || group.deleted ? "已删除" : group.enabled ? "启用" : "停用"} · {group.categories.join(", ") || "未设置分类"}</small></span><span class="fetch-group-stats"><small>检查 {group.last_checked_day || "—"}</small><small>历史 {group.last_release_day || "—"} · 新增 {group.new_count ?? group.fetched_count ?? 0}</small></span></button>)}</nav>
      {selected && <article class="fetch-group-editor" key={selected.group_uuid ?? selected.id ?? `new-${selectedIndex}`}>
        <div class="fetch-group-summary"><span>最近检查 <strong>{selected.last_checked_day || "暂无"}</strong></span><span>历史游标 <strong>{selected.last_release_day || "暂无"}</strong></span><span>累计新增 <strong>{selected.new_count ?? selected.fetched_count ?? 0}</strong></span></div>
        <label>名称<input maxLength={80} value={selected.name} disabled={showDeleted} onInput={(event) => update({ name: event.currentTarget.value })} /></label>
        <label>唯一简称（slug）<input minLength={3} maxLength={48} pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={selected.slug || ""} disabled={showDeleted} onInput={(event) => update({ slug: event.currentTarget.value.toLowerCase() })} /><small>仅限小写英文字母、数字和单个横杠；可修改，真实身份 UUID 不变。</small></label>
        <label>研究背景<textarea rows={5} value={selected.research_profile} disabled={showDeleted} onInput={(event) => update({ research_profile: event.currentTarget.value })} /></label>
        {!showDeleted && <label class="check-label"><input type="checkbox" checked={selected.enabled} onChange={(event) => update({ enabled: event.currentTarget.checked })} />启用此组</label>}
        {!showDeleted && <details class="fetch-group-advanced"><summary>高级设置：分类、权重与排除词</summary><label>arXiv 分类（最多 10 个，逗号分隔）<input value={selected.categories.join(", ")} onInput={(event) => update({ categories: event.currentTarget.value.split(",").map((value) => value.trim()).filter(Boolean) })} /></label><label>关键词与权重（每行“词:权重”，0.5–10）<textarea rows={6} value={Object.entries(selected.keywords).map(([word, weight]) => `${word}:${weight}`).join("\n")} onInput={(event) => update({ keywords: Object.fromEntries(event.currentTarget.value.split("\n").map((line) => { const split = line.lastIndexOf(":"); const word = (split < 0 ? line : line.slice(0, split)).trim(); const weight = split < 0 ? 1 : Number(line.slice(split + 1)); return [word, Number.isFinite(weight) ? weight : 1] as const; }).filter(([word]) => word)) })} /></label><label>排除词（每行一个，最多 20 个）<textarea rows={5} value={(selected.exclude_keywords ?? []).join("\n")} onInput={(event) => update({ exclude_keywords: event.currentTarget.value.split("\n").map((value) => value.trim()).filter(Boolean) })} /></label></details>}
        <div class="actions">{showDeleted ? <><button type="button" class="btn btn-primary" onClick={() => void restore()}>恢复</button><button type="button" class="btn btn-ghost danger" onClick={() => void purge()}>永久清除</button><button type="button" class="btn btn-ghost" onClick={() => void loadHistory()}>查看保留的账本</button></> : <><button type="button" class="btn btn-primary" onClick={() => void save()}>{identifier(selected) === undefined ? "确认并创建" : "保存此组"}</button><button type="button" class="btn btn-ghost" disabled={!canRun} title={!canRun ? "请先启用并保存这个抓取组" : undefined} onClick={() => void run()}>立即抓取</button><button type="button" class="btn btn-ghost" onClick={() => void loadHistory()}>查看日期记录</button><button type="button" class="btn btn-ghost" onClick={copy}>复制</button><button type="button" class="btn btn-ghost danger" onClick={() => void remove()}>删除</button></>}</div>
        {history && <div class="fetch-day-history"><h3>最近日期记录</h3>{history.length === 0 ? <p class="muted-text">暂无记录</p> : <ul>{history.map((entry, index) => <li key={`${String(entry.day)}-${index}`}><strong>{String(entry.day)}</strong><span>{entry.status === "nonempty" ? "有发布" : entry.status === "empty" ? "空日期" : String(entry.status || "已检查")}</span><small>匹配 {String(entry.matched_count ?? 0)} · 新增 {String(entry.new_count ?? 0)}{entry.archived ? " · 历史规则" : ""}</small></li>)}</ul>}</div>}
      </article>}
    </div>
  </section>;
}
